(() => {
  'use strict';
  const node = document.getElementById('nytc-gift-config');
  if (!node || window.nytcExplorerGifts) return;
  const config = JSON.parse(node.textContent);
  const root = window.Shopify?.routes?.root || '/';
  let running = null;
  let queued = false;
  let checkoutPass = false;
  let checkoutReady = false;
  let expressBusy = false;
  let pendingCheckout = null;
  let timer;
  const blocked = new Set(); // gifts that failed for good this page view: never retry in a loop
  const attempts = new Map();
  let passFailures = 0;
  const retryPending = () => config.gifts.some(gift => attempts.has(gift.key) && !blocked.has(gift.key));
  function wantedQuantity(cart, gift) {
    const total = cart.items.filter(item => config.triggers.includes(item.product_id)).reduce((sum, item) => sum + item.quantity, 0);
    if (gift.key !== 'explorer') return total;
    const bundled = cart.items.filter(item => config.bundledUpgrades.includes(item.product_id)).reduce((sum, item) => sum + item.quantity, 0);
    const upgrades = cart.items.filter(item => config.upgradeVariants.includes(item.variant_id)).reduce((sum, item) => sum + item.quantity, 0);
    return Math.max(0, total - bundled - upgrades);
  }
  function giftsReady(cart) {
    return config.gifts.every(gift => {
      const lines = cart.items.filter(item => item.variant_id === gift.id);
      return lines.reduce((sum, item) => sum + item.quantity, 0) === wantedQuantity(cart, gift) && lines.every(item => item.price === 0 && item.final_line_price === 0);
    });
  }
  function status(text) {
    document.querySelectorAll('[data-nytc-gift-status]').forEach(el => { el.textContent = text; });
  }
  function report(reason) {
    // Shows up in Clarity next to the theme's JS diagnostics, so a broken gift is findable.
    try {
      if (window.console && console.warn) console.warn('[NYTC gift] ' + reason);
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'nytc_gift_issue', reason);
        window.clarity('event', 'nytc_gift_issue');
      }
    } catch (_) { /* Diagnostics must not interrupt shopping. */ }
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(root + path, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('Cart request failed: ' + path + ' ' + response.status);
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  // A write can be refused or lost when another script saves the same cart in the same moment.
  // Try again a little later, at most three times, before telling the shopper anything.
  function retryLater(gift, reason) {
    const count = (attempts.get(gift.key) || 0) + 1;
    attempts.set(gift.key, count);
    report(reason + ':' + gift.key + ':' + count);
    if (count >= 3) { blocked.add(gift.key); return; }
    setTimeout(schedule, 700 * count);
  }
  function refresh(cart) {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }
  async function reconcile() {
    checkoutReady = false;
    // Package protection saves to the same cart after every add. Let it finish first:
    // two scripts writing one cart at once can drop one of the writes.
    // (Not on the Buy-it-now path: the shopper never sees the cart there, so no optional fee is added unseen.)
    if (!expressBusy) {
      try {
        await Promise.race([window.nytcPackageProtection?.sync?.(), new Promise(resolve => setTimeout(resolve, 4000))]);
      } catch (_) { /* Protection is optional. */ }
    }
    let cart = await request('cart.js');
    let changed = false;
    let unavailable = false;
    try {
      const attributes = {};
      for (const key of ['_nytc_explorer_gift', '_nytc_suction_gift']) {
        if (cart.attributes?.[key]) attributes[key] = '';
      }
      if (Object.keys(attributes).length) {
        await request('cart/update.js', { attributes });
        cart = await request('cart.js');
        changed = true;
      }
      for (const gift of config.gifts) {
        // Read again after each write so a later step never uses an old snapshot.
        const eligibleQuantity = wantedQuantity(cart, gift);
        const eligible = eligibleQuantity > 0;
        const existing = cart.items.filter(item => item.variant_id === gift.id);
        const updates = {};
        if (!eligible) {
          existing.forEach(item => { updates[item.key] = 0; });
        } else if (existing.length) {
          if (existing[0].quantity !== eligibleQuantity) updates[existing[0].key] = eligibleQuantity;
          existing.slice(1).forEach(item => { updates[item.key] = 0; });
        }
        if (Object.keys(updates).length) {
          await request('cart/update.js', { updates });
          changed = true;
          cart = await request('cart.js');
        } else if (eligible && !existing.length) {
          if (blocked.has(gift.key)) { unavailable = true; continue; }
          try {
            await request('cart/add.js', { items: [{ id: gift.id, quantity: eligibleQuantity, properties: { ['_free_' + gift.key + '_auto']: '1' } }] });
          } catch (error) {
            retryLater(gift, 'add-failed');
            if (blocked.has(gift.key)) unavailable = true;
            continue;
          }
          changed = true;
          cart = await request('cart.js');
          const added = cart.items.filter(item => item.variant_id === gift.id);
          if (!added.length) {
            retryLater(gift, 'add-lost');
            if (blocked.has(gift.key)) unavailable = true;
            continue;
          }
          // Verify or roll back: the gift must ring up at exactly $0, or it does not stay.
          if (added.some(item => item.final_line_price !== 0 || item.price !== 0)) {
            const rollback = {};
            added.forEach(item => { rollback[item.key] = 0; });
            await request('cart/update.js', { updates: rollback });
            cart = await request('cart.js');
            blocked.add(gift.key); unavailable = true; report('not-free:' + gift.key);
            continue;
          }
          attempts.delete(gift.key);
          // The shopper already put a paid one in: make that one the free one instead of charging for it.
          const paid = cart.items.find(item => gift.paidIds.includes(item.variant_id) && item.quantity > 0);
          if (paid) {
            await request('cart/update.js', { updates: { [paid.key]: Math.max(0, paid.quantity - eligibleQuantity) } });
            cart = await request('cart.js');
          }
        }
      }
    } finally {
      // A later failure must not hide a gift that was successfully added.
      if (changed && !expressBusy) refresh(cart);
    }
    checkoutReady = giftsReady(cart);
    status(unavailable || !checkoutReady ? config.error : '');
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(sync, 160); }
  function sync() {
    if (running) { queued = true; return running; }
    running = reconcile().then(() => { passFailures = 0; }).catch(() => {
      // A dropped connection is not the shopper's problem: try again quietly a few times.
      passFailures += 1;
      report('reconcile-failed:' + passFailures);
      if (passFailures < 3) setTimeout(schedule, 1200 * passFailures);
    }).finally(() => {
      running = null;
      if (queued) { queued = false; schedule(); return; }
      resumeCheckout();
    });
    return running;
  }
  // Run one fresh pass after anything already in flight, and wait for it.
  async function settle() {
    if (running) await running;
    clearTimeout(timer); queued = false;
    await sync();
  }
  function resumeCheckout() {
    // A Checkout tap that arrived mid-update is carried out now instead of being swallowed.
    if (!pendingCheckout || running || retryPending()) return;
    const { form, submitter } = pendingCheckout;
    pendingCheckout = null;
    if (!checkoutReady) { status(config.error); return; }
    status('');
    if (!form.isConnected) return;
    checkoutPass = true;
    try { form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined); }
    catch (_) { form.submit(); }
  }
  document.addEventListener('submit', event => {
    if (!(event.target instanceof Element) || !event.target.matches('#CartDrawerForm, #CartPageForm')) return;
    if (checkoutPass) { checkoutPass = false; return; }
    event.preventDefault(); event.stopImmediatePropagation();
    pendingCheckout = { form: event.target, submitter: event.submitter || null };
    status(config.updating);
    blocked.clear(); attempts.clear();
    settle();
  }, true);

  // Buy it now / Shop Pay / More payment options skip the cart, so on a trigger product page they
  // would skip the gift too. Put the item in the cart, let the gift settle, then go to checkout.
  function expressHost(event) {
    const path = event.composedPath ? event.composedPath() : [event.target];
    let actionable = false;
    for (const el of path) {
      if (!(el instanceof Element)) continue;
      if (el.matches('.shopify-payment-button, [data-shopify="payment-button"]')) return actionable ? el : null;
      // Wallet buttons are custom elements (shop-pay-wallet-button, more-payment-options-link, ...) that hide their real button inside.
      if (el.matches('button, a, [role="button"]') || /-(button|link)$/.test(el.localName)) actionable = true;
    }
    return null;
  }
  function formIsTrigger(form) {
    // Shopify puts the product id in every product form, so this works on any page, not just product pages.
    const field = form.querySelector('[name="product-id"]');
    return field ? config.triggers.includes(Number(field.value)) : !!config.pageTrigger;
  }
  function unlockExpress() {
    expressBusy = false;
    document.documentElement.classList.remove('nytc-gift-express');
  }
  async function express(form) {
    if (expressBusy) return;
    expressBusy = true;
    status(config.updating);
    document.documentElement.classList.add('nytc-gift-express');
    try {
      const data = new FormData(form);
      const variantId = Number(data.get('id'));
      const wanted = Math.max(1, Number(data.get('quantity')) || 1);
      // Already in the cart from an earlier Add to cart? Top it up to what was asked for; never double it.
      let have = 0;
      try {
        const cart = await request('cart.js');
        have = cart.items.filter(item => item.variant_id === variantId).reduce((sum, item) => sum + item.quantity, 0);
      } catch (_) { /* Unknown cart: add as asked. */ }
      if (have < wanted) {
        data.set('quantity', String(wanted - have));
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', body: data,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('add failed ' + response.status);
      }
    } catch (_) {
      // Could not add (sold out, offline): hand back to the theme's normal add-to-cart and its own message.
      unlockExpress();
      status('');
      try { form.requestSubmit(form.querySelector('[data-add-to-cart]') || undefined); } catch (__) { /* nothing else to do */ }
      return;
    }
    try {
      for (let round = 0; round < 3; round += 1) {
        await settle();
        if (!retryPending()) break;
        await new Promise(resolve => setTimeout(resolve, 600));
      }
    } catch (_) { report('express-settle-failed'); }
    if (!checkoutReady) { unlockExpress(); status(config.error); return; }
    window.location.assign(root + 'checkout');
    // If the browser never leaves the page, give the buy box back.
    setTimeout(unlockExpress, 10000);
  }
  window.addEventListener('click', event => {
    const host = expressHost(event);
    if (!host) return;
    const form = host.closest('form');
    if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '') || !formIsTrigger(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    express(form);
  }, true);

  ['ajaxProduct:added', 'cart:updated', 'cart:build', 'cart:open', 'page:loaded'].forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', () => { unlockExpress(); schedule(); });
  document.querySelectorAll('#CartDrawer [data-products], #CartPageForm [data-products]').forEach(el => {
    new MutationObserver(schedule).observe(el, { childList: true, subtree: true });
  });
  const drawer = document.getElementById('CartDrawer');
  if (drawer) new MutationObserver(schedule).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  window.nytcExplorerGifts = { sync, settle };
  sync();
})();
